import { EntityValidator } from '@/core/infrastructure/utils/EntityValidator'
import { LearningGrain } from './ClassroomResultTask'
import {
  StudentScoreArraySchema,
  StudentScoreSchema,
  type StudentScoreEntity
} from '@/core/infrastructure/schemas/dashboard/ClassroomResultsSchema'

/**
 * Classe représentant les résultats d'un étudiant pour un module
 */
export class DashboardClassroomStudentResult {
  readonly identifier: string
  readonly firstname: string
  readonly lastname: string
  readonly teacherFirstname: string | null
  readonly teacherLastname: string | null
  readonly taskId: number
  readonly taskTitle: string
  readonly taskOrder: number
  readonly learningModuleId: number
  readonly learningModuleTitle: string
  readonly learningModuleOrder: number
  readonly displayOrder: number
  readonly diagVersionNumber: number | null
  readonly diagScorePercentRounded: number | null
  readonly diagEvalRangeName: string | null
  readonly moduleScorePercentRounded: number | null
  readonly moduleEvalRangeName: string | null
  readonly diagnosticModuleVariation: number | null
  readonly diagEndVersionNumber: number | null
  readonly diagEndScorePercentRounded: number | null
  readonly diagEndEvalRangeName: string | null
  readonly learningGrains: LearningGrain[]

  constructor(data: StudentScoreEntity) {
    this.identifier = data.identifier
    this.firstname = data.firstname
    this.lastname = data.lastname
    this.teacherFirstname = data.teacherFirstname
    this.teacherLastname = data.teacherLastname
    this.taskId = data.taskId
    this.taskTitle = data.taskTitle
    this.taskOrder = data.taskOrder
    this.learningModuleId = data.learningModuleId
    this.learningModuleTitle = data.learningModuleTitle
    this.learningModuleOrder = data.learningModuleOrder
    this.displayOrder = data.displayOrder
    this.diagVersionNumber = data.diagVersionNumber
    this.diagScorePercentRounded = data.diagScorePercentRounded
    this.diagEvalRangeName = data.diagEvalRangeName
    this.moduleScorePercentRounded = data.moduleScorePercentRounded
    this.moduleEvalRangeName = data.moduleEvalRangeName
    this.diagnosticModuleVariation = data.diagnosticModuleVariation
    this.diagEndVersionNumber = data.diagEndVersionNumber
    this.diagEndScorePercentRounded = data.diagEndScorePercentRounded
    this.diagEndEvalRangeName = data.diagEndEvalRangeName
    this.learningGrains = data.learningGrains.map((grain) => LearningGrain.create(grain))
  }

  /**
   * Obtient le nom complet de l'étudiant
   */
  public getFullName(): string {
    return `${this.firstname} ${this.lastname}`
  }

  /**
   * Obtient le nom complet de l'enseignant
   */
  get teacherFullName(): string | null {
    if (!this.teacherFirstname || !this.teacherLastname) return null
    return `${this.teacherFirstname} ${this.teacherLastname}`
  }

  /**
   * Vérifie si l'étudiant a commencé le diagnostic
   */
  public hasStartedDiagnostic(): boolean {
    return this.diagVersionNumber !== null
  }

  /**
   * Vérifie si l'étudiant a terminé le diagnostic
   */
  public hasCompletedDiagnostic(): boolean {
    return this.diagEndVersionNumber !== null
  }

  /**
   * Calcule la progression entre diagnostic début et fin
   */
  public getDiagnosticProgression(): number | null {
    if (!this.hasStartedDiagnostic() || !this.hasCompletedDiagnostic()) return null
    if (!this.diagScorePercentRounded || !this.diagEndScorePercentRounded) return null
    return this.diagEndScorePercentRounded - this.diagScorePercentRounded
  }

  /**
   * Obtient le pourcentage de grains complétés
   */
  public getGrainsCompletionPercent(): number {
    if (this.learningGrains.length === 0) return 0
    const completedGrains = this.learningGrains.filter((grain) => grain.isCompleted())
    return Math.round((completedGrains.length / this.learningGrains.length) * 100)
  }

  /**
   * Crée une instance à partir de données brutes
   */
  public static create(data: unknown): DashboardClassroomStudentResult {
    return EntityValidator.validateAndCreate(
      StudentScoreSchema,
      data,
      DashboardClassroomStudentResult,
      'DashboardClassroomStudentResult'
    )
  }

  /**
   * Crée plusieurs instances à partir de données brutes
   */
  public static createMany(data: unknown[]): DashboardClassroomStudentResult[] {
    return EntityValidator.validateAndCreateMany(
      StudentScoreArraySchema,
      data,
      DashboardClassroomStudentResult,
      'DashboardClassroomStudentResult'
    )
  }
}
